import React, { ReactElement, useEffect, useState } from "react";
import styled from "styled-components";

import { VoteStatus } from "types/voteEnum";

import OptionsWrapper from "./OptionsWrapper";
import ActionSection from "../../common/ActionSection";
import TitleHeader from "../../common/TitleHeader";
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Box,
    Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Divider from "@mui/material/Divider";

type Props = {
    onValidate: (answer: string | string[]) => Promise<any>;
    subTitles: string[];
    options: { id: string; text: string }[];
    title: string;
    isAssociationQuestion?: boolean;
};

const GroupedQuestion = ({
    onValidate,
    options,
    title,
    subTitles,
    isAssociationQuestion = false,
}: Props) => {
    const [answers, setAnswers] = useState<(string | undefined)[]>(
        Array(subTitles.length).fill(undefined),
    );
    const [voteStatus, setVoteStatus] = useState(VoteStatus.Idle);
    const [expandedAccordions, setExpandedAccordions] = useState<boolean[]>(
        Array(subTitles.length).fill(true),
    );

    useEffect(() => {
        if (
            answers.filter((a) => a !== undefined).length === subTitles.length
        ) {
            setVoteStatus(VoteStatus.PendingValidation);
        }
    }, [answers, subTitles.length]);

    const handleValidate = async () => {
        setVoteStatus(VoteStatus.Loading);
        try {
            await onValidate(answers.join(""));
            setVoteStatus(VoteStatus.Success);
        } catch {
            setVoteStatus(VoteStatus.Error);
        }
    };

    const handleReset = () => {
        setVoteStatus(VoteStatus.Idle);
        setAnswers(Array(subTitles.length).fill(undefined));
    };

    const selectedAnswers = answers.filter((a) => a !== undefined) as string[];

    const renderQuestions = (): ReactElement => (
        <>
            {subTitles.map((subtitle, index) => {
                // Disabled other item in association question
                const disabledOptions = isAssociationQuestion
                    ? selectedAnswers.filter((ans) => ans !== answers[index])
                    : [];

                return (
                    <Accordion
                        key={index}
                        disableGutters
                        elevation={4}
                        expanded={expandedAccordions[index]}
                        onChange={(
                            event: React.SyntheticEvent,
                            isExpanded: boolean,
                        ) => {
                            const updated = [...expandedAccordions];
                            updated[index] = isExpanded;
                            setExpandedAccordions(updated);
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls={`panel${index}-content`}
                            id={`panel${index}-header`}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                }}
                            >
                                <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: "bold" }}
                                >
                                    {subtitle}
                                </Typography>
                                {answers[index] && (
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        {
                                            options.find(
                                                (o) => o.id === answers[index],
                                            )?.text
                                        }
                                    </Typography>
                                )}
                            </Box>
                        </AccordionSummary>
                        <Divider />
                        <AccordionDetails sx={{ pt: 0 }}>
                            <OptionsWrapper
                                index={index + 1}
                                options={options}
                                answers={answers}
                                setAnswers={setAnswers}
                                setVoteStatus={setVoteStatus}
                                disabledOptions={disabledOptions}
                                closeAccordion={
                                    isAssociationQuestion
                                        ? () => {
                                              const updated = [
                                                  ...expandedAccordions,
                                              ];
                                              updated[index] = false;
                                              setExpandedAccordions(updated);
                                          }
                                        : undefined
                                }
                            />
                        </AccordionDetails>
                    </Accordion>
                );
            })}
        </>
    );

    return (
        <WrapperVote>
            <TitleHeader title={title} />
            {renderQuestions()}
            <ActionSection
                voteStatus={voteStatus}
                handleValidate={handleValidate}
                handleReset={handleReset}
            />
        </WrapperVote>
    );
};

const WrapperVote = styled.div`
    @media (max-width: 768px) {
        max-width: 350px;
    }
    > span {
        font-size: 1.3rem;
    }
`;

export default GroupedQuestion;
